<template>
  <div
    class="german-bi-form"
    data-testid="germanBiForm"
  >
    <div class="german-bi-form__options">
      <AkRadio
        v-for="option in businessTypeOptions"
        :key="option.value"
        :is-checked="selectedType === option.value"
        :text="option.label"
        :value="option.value"
        boxed
        class="german-bi-form__option"
        name="businessType"
        data-testid="businessTypeRadio"
        @change="handleTypeChange(option.value)"
      />
    </div>

    <div class="german-bi-form__content">
      <Transition
        name="form-slide"
        mode="out-in"
      >
        <BusinessRegistrySearch
          v-if="selectedType === BusinessType.HasHandelsregister"
          :key="BusinessType.HasHandelsregister"
          v-model="registryNumber"
          :errors="registryValidation.$error ? registryValidation.$errors : []"
          data-testid="registrySearch"
          @select="handleRegistrySelect"
          @use-it-anyway="handleUseItAnyway"
          @input-change="handleRegistryInputChange"
          @blur="handleRegistryBlur"
        />

        <AkInput
          v-else-if="selectedType === BusinessType.SoleTrader"
          :key="BusinessType.SoleTrader"
          v-model="soleTraderNumber"
          :label="t('retailer.germanBusinessIdentifier.soleTrader.numberLabel')"
          :placeholder="t('retailer.germanBusinessIdentifier.soleTrader.numberPlaceholder')"
          :errors="soleTraderValidation.$errors"
          size="medium"
          data-testid="soleTraderNumberInput"
          @blur="handleSoleTraderBlur"
        />
      </Transition>

      <HqAddressForm
        v-if="useItAnyway && selectedType === BusinessType.HasHandelsregister"
        data-testid="hqAddressForm"
        @update="handleManualHqAddressUpdate"
      />
    </div>

    <AkAlert
      v-if="submitError"
      :type="AlertType.Error"
      :closable="false"
      class="german-bi-form__error"
      data-testid="submitErrorAlert"
    >
      {{ submitError }}
    </AkAlert>

    <AkButton
      size="lg"
      class="german-bi-form__submit"
      :disabled="!canSubmit || isSubmitting"
      :loading="isSubmitting"
      data-testid="submitButton"
      @click="handleSubmitClick"
    >
      {{ t('retailer.germanBusinessIdentifier.modal.submit') }}
    </AkButton>
  </div>
</template>

<script setup lang="ts">
import { AkButton, AkInput, AkRadio, AkAlert, AlertType } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import { useGermanBi } from '../composables/use-german-bi';
import { BusinessType } from '../types/german-bi.types';
import BusinessRegistrySearch from './business-registry-search.vue';
import HqAddressForm from './hq-address-form.vue';

defineOptions({ name: 'GermanBiForm' });

const { t } = useI18n();

const emit = defineEmits<{
  submitted: [];
}>();

const {
  selectedType,
  registryNumber,
  soleTraderNumber,
  isSubmitting,
  submitError,
  registryValidation,
  businessTypeOptions,
  canSubmit,
  useItAnyway,
  selectedRegistryResult,
  manualHqAddress,
  soleTraderValidation,
  handleTypeChange,
  handleRegistrySelect,
  handleUseItAnyway,
  handleManualHqAddressUpdate,
  handleSubmit,
  reset,
  trackSoleTraderNumberEntered,
} = useGermanBi();

async function handleSubmitClick(): Promise<void> {
  const success = await handleSubmit();
  if (success) {
    emit('submitted');
  }
}

function handleSoleTraderBlur(): void {
  soleTraderValidation.$touch();
  trackSoleTraderNumberEntered(!!soleTraderNumber.value);
}

function handleRegistryBlur(): void {
  registryValidation.$touch();
}

function handleRegistryInputChange(): void {
  selectedRegistryResult.value = null;
  if (useItAnyway.value) {
    useItAnyway.value = false;
    manualHqAddress.value = null;
  }
}

defineExpose({ reset });
</script>

<style scoped lang="scss">
.german-bi-form {
  @apply ds-flex ds-flex-col;

  &__options {
    @apply ds-flex ds-flex-col ds-gap-3 ds-mb-6;
  }

  &__option {
    @apply ds-w-full ds-text-left;

    :deep(.ak-radio__box) {
      @apply ds-w-full;
    }

    :deep(.ak-radio__box--checked) {
      --ak-radio-accent-color: theme('colors.accent.700');
    }
  }

  &__content {
    @apply ds-mb-6;
  }

  &__error {
    @apply ds-mb-4;
  }

  &__submit {
    @apply ds-w-full;
  }
}

.form-slide-enter-active,
.form-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.form-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.form-slide-enter-to,
.form-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
